import React, { useState, useEffect, lazy, Suspense } from 'react';
import api from '../services/ApiNew';
import './Dashboard.css';


// This file is NOT loaded until needed
const CallbackRequests = lazy(() => import('./CallbackRequests'));
const SubscriptionPage = lazy(() => import('./SubscriptionPageRazorPay'));
const LeadRequests = lazy(() => import('./LeadRequests'));
const ProviderTenders = lazy(() => import('./ProviderTenders'));
const MyBids = lazy(() => import('./MyBids'));
const WalletRecharge = lazy(() => import('./WalletRecharge'));
const ProviderWorkPhotos = lazy(() => import('./ProviderWorkPhotos'));



const ProviderDashboard = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [activeTab, setActiveTab] = useState('tenders');
	const [profile, setProfile] = useState(null);
	const [isUpdating, setIsUpdating] = useState(false);
	const [error, setError] = useState(false); // Track if request failed

	const fetchProfile = async () => {
		try {
			const res = await api.get('/api/v1/provider/me');
			setProfile(res.data);
			setActiveTab('tenders');
		} catch (err) {
			console.error("Error fetching profile", err);
			setError(true);
		}
	};

	useEffect(() => { fetchProfile(); }, []);

	const handleUpdate = async (e) => {
		e.preventDefault();
		setIsUpdating(true);
		try {
			// Ensure data types match your Java POJO (ints/doubles)
			const payload = {
				...profile,
				experience: parseInt(profile.experience),
				charge: parseFloat(profile.charge)
			};
			await api.put('/api/v1/provider/me', payload);
			alert("Profile updated successfully!");
			setActiveTab('view');
			fetchProfile(); // Refresh data
		} catch (err) {
			alert("Update failed. Check backend logs.");
		} finally { setIsUpdating(false); }
	};

	const handleDeactivate = async () => {
		if (window.confirm("Are you sure? This will hide your profile from customers.")) {
			try {
				setIsUpdating(true);
				await api.delete('/api/v1/provider/me');
				handleLogout();
			} catch (err) { alert("Deactivation failed"); }
			finally { setIsUpdating(false); }
		}
	};

	const handleActivate = async () => {
		if (window.confirm("Are you sure? Do you want to activate your account!")) {
			try {
				setIsUpdating(true);
				const res = await api.put('/api/v1/provider/me/activate');
				if (res.status === 200) {
					setProfile(prev => ({ ...prev, active: true }))
				}
			} catch (err) { alert("Activation failed"); }
			finally { setIsUpdating(false); }
		}
	};


	const handleLogout = () => {
		localStorage.removeItem('accessToken');
		localStorage.removeItem('refreshToken');
		window.location.href = '/login';
	};

	if (error) return <div>Failed to load profile. Please refresh.</div>;
	if (!profile) return (
		<div className="registration-container">
			<div className="loader"></div>
		</div>
	);

	return (
		<div className="dashboard-container">
			{/* Hamburger Toggle - Always accessible */}
			<button
				className="hamburger-btn"
				onClick={() => setIsOpen(!isOpen)}
				style={{ left: isOpen ? '210px' : '20px' }}
				aria-label={isOpen ? 'Close sidebar menu' : 'Open sidebar menu'}
				title={isOpen ? 'Close menu' : 'Open menu'}
			>
				{isOpen ? '✕ Close Menu' : '☰ Menu'}
			</button>

			{/* Sidebar */}
			<aside className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
				<div className="sidebar-header">
					<span>ServiceDekho</span>
				</div>
				<nav>
					<button className={activeTab === 'view' ? 'active' : ''} onClick={() => setActiveTab('view')}>
						👤 Profile Overview
					</button>

					<button className={activeTab === 'callbacks' ? 'active' : ''} onClick={() => setActiveTab('callbacks')}>
						📞 Callback Requests
					</button>

					<button className={activeTab === 'leads' ? 'active' : ''} onClick={() => setActiveTab('leads')}>
						📞 Leads Requests
					</button>

					<button className={activeTab === 'tenders' ? 'active' : ''} onClick={() => setActiveTab('tenders')}>
						📋 Live Tenders
					</button>

					<button className={activeTab === 'mybids' ? 'active' : ''} onClick={() => setActiveTab('mybids')}>
						🏆 My Bids
					</button>

					<button className={activeTab === 'wallet' ? 'active' : ''} onClick={() => setActiveTab('wallet')}>
						💰 Wallet ({profile.points ?? 0} pts)
					</button>


					<button className={activeTab === 'edit' ? 'active' : ''} onClick={() => setActiveTab('edit')}>
						✏️ Edit Profile
					</button>

					<button className={activeTab === 'workphotos' ? 'active' : ''} onClick={() => setActiveTab('workphotos')}>
						📷 Work Photos
					</button>

					<button
						className={activeTab === 'subscription' ? 'active' : ''}
						onClick={() => setActiveTab('subscription')}
					>
						💳 Subscription Plans
					</button>

					<button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>
						⚙️ Account Settings
					</button>
					<button className="logout-link" onClick={handleLogout}>
						🚪 Logout
					</button>
				</nav>
			</aside>

			{/* Main Content Area */}
			<main className={`main-content ${isOpen ? 'pushed' : 'full'}`}>
				{activeTab === 'view' && (
					<section className="card animate-fade-in">
						<h3>My Profile</h3>
						{!profile.premium && (
							<div className="upgrade-banner" onClick={() => setActiveTab('subscription')} role="button" tabIndex={0}
								onKeyDown={(e) => e.key === 'Enter' && setActiveTab('subscription')}>
								<span>Upgrade to Premium for more leads and WhatsApp delivery.</span>
								<button type="button" className="upgrade-banner-btn">View Plans</button>
							</div>
						)}
						<div className="info-grid">
							<div className="info-item"><span>Name:</span> {profile.name}</div>
							<div className="info-item"><span>Email:</span> {profile.email}</div>
							<div className="info-item"><span>Service:</span> {profile.serviceType}</div>
							<div className="info-item"><span>Experience:</span> {profile.experience} yrs</div>
							<div className="info-item"><span>Charges:</span> ₹{profile.charge} / {profile.chargeType}</div>
							<div className="info-item"><span>City:</span> {profile.city}</div>
							<div className="info-item">
								<span>Premium:</span> {profile.premium ? "Yes" : "No"}
							</div>
							<div className="info-item"><span>Wallet Points:</span> {profile.points ?? 0}</div>
							<div className="info-item"><span>Balance:</span> ₹{profile.balance ?? 0}</div>
						</div>
					</section>
				)}

				{activeTab === 'edit' && (
					<form className="card animate-fade-in" onSubmit={handleUpdate}>
						<h3>Update Profile</h3>
						<div className="form-group">
							<label>Full Name</label>
							<input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} required />
						</div>
						<div className="form-group">
							<label>Experience (Years)</label>
							<input type="number" value={profile.experience} onChange={(e) => setProfile({ ...profile, experience: e.target.value })} required />
						</div>
						<div className="form-group">
							<label>Hourly/Daily Charge (₹)</label>
							<input type="number" step="0.01" value={profile.charge} onChange={(e) => setProfile({ ...profile, charge: e.target.value })} required />
						</div>
						<button type="submit" className="submit-btn" disabled={isUpdating}>
							{isUpdating ? <div className="loader"></div> : "Save Changes"}
						</button>
					</form>
				)}

				{activeTab === 'settings' && (
					<div className="card animate-fade-in">
						<h3 style={{ color: '#e53e3e' }}>Danger Zone</h3>
						<p>Deactivating your account will hide your services from search results. You can reactivate it later by contacting support.</p>
						{profile.active ? (
							<button className="btn-danger" onClick={handleDeactivate} disabled={isUpdating} >
								{isUpdating ? "Processing..." : "Deactivate My Account"}</button>)
							: (<button className="btn-primary" onClick={handleActivate} disabled={isUpdating}>
								{isUpdating ? 'Processing...' : "Activate My Account"}
							</button>)

						}
					</div>
				)}

				{/*{activeTab === 'subscription' && <SubscriptionPage />} */}

				{activeTab === 'subscription' && (
					<Suspense fallback={
						<div className="inline-loader">
							<div className="loader-spinner"></div>
							<span>Loading Subscriptions...</span>
						</div>
					}>
						<SubscriptionPage />
					</Suspense>
				)}

				{activeTab === 'callbacks' && (
					<Suspense fallback={
						<div className="inline-loader">
							<div className="loader-spinner"></div>
							<span>Loading Requests...</span>
						</div>
					}>
						<CallbackRequests setActiveTab={setActiveTab} />
					</Suspense>
				)}

				{activeTab === 'leads' && (
					<Suspense fallback={
						<div className="inline-loader">
							<div className="loader-spinner"></div>
							<span>Loading Requests...</span>
						</div>
					}>
						<LeadRequests provider={profile} setActiveTab={setActiveTab} />
					</Suspense>
				)}

				{activeTab === 'tenders' && (
					<Suspense fallback={<div className="inline-loader"><div className="loader-spinner"></div><span>Loading Tenders...</span></div>}>
						<ProviderTenders provider={profile} setActiveTab={setActiveTab} />
					</Suspense>
				)}

				{activeTab === 'mybids' && (
					<Suspense fallback={<div className="inline-loader"><div className="loader-spinner"></div><span>Loading Bids...</span></div>}>
						<MyBids />
					</Suspense>
				)}

				{activeTab === 'wallet' && (
					<Suspense fallback={<div className="inline-loader"><div className="loader-spinner"></div><span>Loading Wallet...</span></div>}>
						<WalletRecharge profile={profile} onRechargeSuccess={fetchProfile} />
					</Suspense>
				)}

				{activeTab === 'workphotos' && (
					<Suspense fallback={<div className="inline-loader"><div className="loader-spinner"></div><span>Loading...</span></div>}>
						<ProviderWorkPhotos />
					</Suspense>
				)}

			</main>
		</div>
	);
};

export default ProviderDashboard;
